import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import "./OpsBilling.css";
import ConfirmDialog from "../../components/ConfirmDialog";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import OpsOverridesTab from "./OpsOverridesTab";

const TABS = {
  plans: "Plan Catalog",
  customers: "Customer Accounts",
  overrides: "Overrides & Promos",
};

const overrideDefaults = {
  customPlanName: "",
  devicesAllowed: "",
  usersAllowed: "",
  smsQuota: "",
  voiceQuota: "",
  priceOverride: "",
  discountPercent: "",
  note: "",
  expiresAt: "",
};

const promoDefaults = {
  code: "",
  description: "",
  maxRedemptions: "",
  perCompanyLimit: "1",
  validFrom: "",
  validUntil: "",
  isActive: true,
  benefits: {
    customPlanName: "",
    devicesAllowed: "",
    usersAllowed: "",
    smsQuota: "",
    voiceQuota: "",
    priceOverride: "",
    discountPercent: "",
    note: "",
    durationDays: "",
    expiresAt: "",
  },
};

export default function OpsBilling() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [activeTab, setActiveTab] = useState("plans");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);

  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [customerStatus, setCustomerStatus] = useState("");
  const { confirmProps, openConfirm } = useConfirmDialog();
  const [overrideSearch, setOverrideSearch] = useState("");
  const [overrideCompanies, setOverrideCompanies] = useState([]);
  const [overrideCompanyLoading, setOverrideCompanyLoading] = useState(false);
  const [overrideCompanyError, setOverrideCompanyError] = useState("");
  const [overrideCompany, setOverrideCompany] = useState(null);
  const [activeOverride, setActiveOverride] = useState(null);
  const [overrideForm, setOverrideForm] = useState(overrideDefaults);
  const [overrideStatus, setOverrideStatus] = useState("");
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoStatus, setPromoStatus] = useState("");
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [promoForm, setPromoForm] = useState(promoDefaults);
  const [promoRedeemCode, setPromoRedeemCode] = useState("");

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "overrides") {
      loadPromoCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/plans`, { headers });
      setPlans(data);
      setError("");
    } catch (err) {
      console.error("OpsBilling:fetch error", err.response?.data || err.message);
      setError("Unable to load plans.");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event, planId) => {
    event.dataTransfer.setData("text/plain", planId);
  };

  const handleDrop = async (event, targetId) => {
    event.preventDefault();
    const draggedId = event.dataTransfer.getData("text/plain");
    if (!draggedId || draggedId === targetId) return;

    const newOrder = [...plans];
    const draggedIndex = newOrder.findIndex((plan) => plan.id === draggedId);
    const targetIndex = newOrder.findIndex((plan) => plan.id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;

    const [draggedPlan] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedPlan);
    setPlans(newOrder);
    await persistOrder(newOrder);
  };

  const persistOrder = async (orderedPlans) => {
    setSavingOrder(true);
    try {
      await axios.post(
        `${API_BASE_URL}/ops/plans/reorder`,
        { planIds: orderedPlans.map((plan) => plan.id) },
        { headers }
      );
    } catch (err) {
      console.error("OpsBilling:reorder error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to update plan order.");
    } finally {
      setSavingOrder(false);
    }
  };

  const openEditor = (plan) => {
    setEditingPlan(plan.id);
    setFormValues({
      ...plan,
      featuresText: (plan.features || []).join("\n"),
      prorateMigrations: false,
    });
    setPlanStatus(null);
  };

  const closeEditor = () => {
    setEditingPlan(null);
    setFormValues(null);
    setPlanStatus(null);
  };

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const savePlan = async () => {
    if (!formValues) return;
    try {
      const payload = {
        ...formValues,
        price: Number(formValues.price),
        devices_allowed: Number(formValues.devices_allowed),
        users_allowed: Number(formValues.users_allowed),
        overage_per_unit: Number(formValues.overage_per_unit || 0),
        sms_quota: Number(formValues.sms_quota || 0),
        voice_quota: Number(formValues.voice_quota || 0),
        display_order: formValues.display_order || 0,
        features: (formValues.featuresText || "")
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean),
        prorate_migrations: Boolean(formValues.prorateMigrations),
      };

      const { data } = await axios.put(`${API_BASE_URL}/ops/plans/${formValues.id}`, payload, {
        headers,
      });

      const migration = data?.migration;
      setPlanStatus({
        type: "success",
        text: migration
          ? `Plan saved. ${migration.migrated}/${migration.total} subscriptions updated${
              migration.failures && migration.failures.length
                ? ` (${migration.failures.length} failed)`
                : ""
            }.`
          : "Plan saved.",
        failures: migration?.failures || [],
        prorated: Boolean(data?.prorated),
      });

      setFormValues((prev) => ({
        ...prev,
        ...data,
        featuresText: ((data?.features ?? prev.features) || []).join("\n"),
        prorateMigrations: prev.prorateMigrations,
      }));
      fetchPlans();
    } catch (err) {
      console.error("OpsBilling:update error", err.response?.data || err.message);
      const message = err.response?.data?.error || "Unable to update plan.";
      setError(message);
      setPlanStatus({
        type: "error",
        text: message,
      });
    }
  };

  const searchCustomers = async () => {
    if (!customerSearch) {
      setCustomerResults([]);
      return;
    }
    try {
      setCustomerLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/companies`, {
        headers,
        params: { search: customerSearch },
      });
      setCustomerResults(data);
      setCustomerError("");
    } catch (err) {
      console.error("OpsBilling:customer search error", err.response?.data || err.message);
      setCustomerError("Unable to search companies.");
    } finally {
      setCustomerLoading(false);
    }
  };

  const loadSubscription = async (company) => {
    if (!company) return;
    try {
      setCustomerLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/companies/${company.id}/subscription`, {
        headers,
      });
      setSubscription(data);
      setSelectedCompany(company);
      setCustomerError("");
    } catch (err) {
      console.error("OpsBilling:subscription fetch error", err.response?.data || err.message);
      setCustomerError("Unable to load subscription.");
    } finally {
      setCustomerLoading(false);
    }
  };

  const performCancellation = async (immediate) => {
    if (!selectedCompany) return;
    try {
      const response = await axios.post(
        `${API_BASE_URL}/ops/companies/${selectedCompany.id}/subscription/cancel`,
        { immediate },
        { headers }
      );
      setCustomerStatus(
        immediate ? "Subscription cancelled immediately." : "Subscription will end at period end."
      );
      if (response) {
        loadSubscription(selectedCompany);
      }
    } catch (err) {
      console.error("OpsBilling:cancel error", err.response?.data || err.message);
      setCustomerError(err.response?.data?.error || "Unable to cancel subscription.");
    }
  };

  const runCustomerAction = async (action) => {
    if (!selectedCompany) return;
    setCustomerStatus("");
    try {
      let response;
      switch (action.type) {
        case "refresh":
          response = await axios.post(
            `${API_BASE_URL}/ops/companies/${selectedCompany.id}/subscription/refresh`,
            {},
            { headers }
          );
          setCustomerStatus("Subscription refreshed.");
          break;
        case "cancel":
          openConfirm({
            title: "Cancel Subscription",
            message:
              "Choose \"Cancel Immediately\" to stop service now, or \"End After Period\" to finish the current billing cycle. Close this dialog to keep the subscription active.",
            confirmLabel: "Cancel Immediately",
            cancelLabel: "Close",
            alternateLabel: "End After Period",
            variant: "danger",
            onConfirm: () => performCancellation(true),
            onAlternate: () => performCancellation(false),
          });
          return;
        case "resume":
          response = await axios.post(
            `${API_BASE_URL}/ops/companies/${selectedCompany.id}/subscription/resume`,
            {},
            { headers }
          );
          setCustomerStatus("Subscription resume scheduled.");
          break;
        case "portal":
          response = await axios.post(
            `${API_BASE_URL}/ops/companies/${selectedCompany.id}/portal-link`,
            {},
            { headers }
          );
          await navigator.clipboard.writeText(response.data.url);
          setCustomerStatus("Billing portal link copied.");
          break;
        default:
          break;
      }
      if (response) {
        loadSubscription(selectedCompany);
      }
    } catch (err) {
      console.error("OpsBilling:customer action error", err.response?.data || err.message);
      setCustomerError(err.response?.data?.error || "Unable to complete action.");
    }
  };

  const searchOverrideCompanies = async () => {
    if (!overrideSearch) {
      setOverrideCompanies([]);
      return;
    }
    try {
      setOverrideCompanyLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/companies`, {
        headers,
        params: { search: overrideSearch },
      });
      setOverrideCompanies(data);
      setOverrideCompanyError("");
    } catch (err) {
      console.error("OpsBilling:override search error", err.response?.data || err.message);
      setOverrideCompanyError("Unable to search companies.");
    } finally {
      setOverrideCompanyLoading(false);
    }
  };

  const hydrateOverrideForm = (data) => ({
    customPlanName: data?.custom_plan_name || "",
    devicesAllowed: data?.devices_allowed ?? "",
    usersAllowed: data?.users_allowed ?? "",
    smsQuota: data?.sms_quota ?? "",
    voiceQuota: data?.voice_quota ?? "",
    priceOverride: data?.price_override ?? "",
    discountPercent: data?.discount_percent ?? "",
    note: data?.note || "",
    expiresAt: data?.expires_at ? data.expires_at.substring(0, 16) : "",
  });

  const selectOverrideCompany = async (company) => {
    setOverrideCompany(company);
    setOverrideStatus("");
    setActiveOverride(null);
    setOverrideForm(overrideDefaults);
    if (!company) return;
    try {
      setOverrideCompanyLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/plan-overrides`, {
        headers,
        params: { companyId: company.id },
      });
      const override = Array.isArray(data) ? data[0] : null;
      setActiveOverride(override);
      setOverrideForm(override ? hydrateOverrideForm(override) : overrideDefaults);
    } catch (err) {
      console.error("OpsBilling:override fetch error", err.response?.data || err.message);
      setOverrideCompanyError("Unable to load override for company.");
    } finally {
      setOverrideCompanyLoading(false);
    }
  };

  const handleOverrideChange = (field, value) => {
    setOverrideForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveOverride = async () => {
    if (!overrideCompany) return;
    try {
      setOverrideSaving(true);
      const payload = {
        companyId: overrideCompany.id,
        ...overrideForm,
      };
      await axios.post(`${API_BASE_URL}/ops/plan-overrides`, payload, { headers });
      setOverrideStatus("Override saved.");
      await selectOverrideCompany(overrideCompany);
    } catch (err) {
      console.error("OpsBilling:override save error", err.response?.data || err.message);
      setOverrideStatus(err.response?.data?.error || "Unable to save override.");
    } finally {
      setOverrideSaving(false);
    }
  };

  const deleteOverride = async () => {
    if (!activeOverride) return;
    openConfirm({
      title: "Remove Override",
      message: `Remove the override for ${overrideCompany?.name || "this company"}?`,
      variant: "danger",
      confirmLabel: "Remove",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/ops/plan-overrides/${activeOverride.id}`, { headers });
          setOverrideStatus("Override removed.");
          setActiveOverride(null);
          setOverrideForm(overrideDefaults);
        } catch (err) {
          console.error("OpsBilling:override delete error", err.response?.data || err.message);
          setOverrideStatus(err.response?.data?.error || "Unable to remove override.");
        }
      },
    });
  };

  const redeemPromo = async () => {
    if (!overrideCompany || !promoRedeemCode) return;
    try {
      setOverrideSaving(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/ops/promo-codes/${promoRedeemCode}/redeem`,
        { companyId: overrideCompany.id },
        { headers }
      );
      setOverrideStatus(`Promo ${promoRedeemCode.toUpperCase()} applied.`);
      setPromoRedeemCode("");
      if (data?.override) {
        setActiveOverride(data.override);
        setOverrideForm(hydrateOverrideForm(data.override));
      } else {
        await selectOverrideCompany(overrideCompany);
      }
    } catch (err) {
      console.error("OpsBilling:promo redeem error", err.response?.data || err.message);
      setOverrideStatus(err.response?.data?.error || "Unable to redeem promo.");
    } finally {
      setOverrideSaving(false);
    }
  };

  const loadPromoCodes = async () => {
    try {
      setPromoLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/ops/promo-codes`, { headers });
      setPromoCodes(data || []);
    } catch (err) {
      console.error("OpsBilling:promo list error", err.response?.data || err.message);
      setPromoStatus("Unable to load promo codes.");
    } finally {
      setPromoLoading(false);
    }
  };

  const resetPromoForm = () => {
    setPromoForm(promoDefaults);
    setEditingPromoId(null);
    setPromoStatus("");
  };

  const openPromoEditor = (promo) => {
    if (!promo) {
      resetPromoForm();
      return;
    }
    setEditingPromoId(promo.id);
    const benefits = promo.benefits || {};
    setPromoForm({
      code: promo.code || "",
      description: promo.description || "",
      maxRedemptions: promo.max_redemptions ?? "",
      perCompanyLimit: promo.per_company_limit ?? "1",
      validFrom: promo.valid_from ? promo.valid_from.substring(0, 16) : "",
      validUntil: promo.valid_until ? promo.valid_until.substring(0, 16) : "",
      isActive: promo.is_active !== false,
      benefits: {
        customPlanName: benefits.custom_plan_name || "",
        devicesAllowed: benefits.devices_allowed ?? "",
        usersAllowed: benefits.users_allowed ?? "",
        smsQuota: benefits.sms_quota ?? "",
        voiceQuota: benefits.voice_quota ?? "",
        priceOverride: benefits.price_override ?? "",
        discountPercent: benefits.discount_percent ?? "",
        note: benefits.note || "",
        durationDays: benefits.duration_days ?? "",
        expiresAt: benefits.expires_at ? benefits.expires_at.substring(0, 16) : "",
      },
    });
  };

  const handlePromoField = (field, value) => {
    setPromoForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePromoBenefitField = (field, value) => {
    setPromoForm((prev) => ({
      ...prev,
      benefits: {
        ...prev.benefits,
        [field]: value,
      },
    }));
  };

  const savePromo = async () => {
    try {
      setPromoLoading(true);
      const payload = {
        code: promoForm.code,
        description: promoForm.description,
        maxRedemptions: promoForm.maxRedemptions,
        perCompanyLimit: promoForm.perCompanyLimit,
        validFrom: promoForm.validFrom || null,
        validUntil: promoForm.validUntil || null,
        isActive: promoForm.isActive,
        benefits: {
          custom_plan_name: promoForm.benefits.customPlanName || undefined,
          devices_allowed:
            promoForm.benefits.devicesAllowed === "" ? undefined : promoForm.benefits.devicesAllowed,
          users_allowed:
            promoForm.benefits.usersAllowed === "" ? undefined : promoForm.benefits.usersAllowed,
          sms_quota:
            promoForm.benefits.smsQuota === "" ? undefined : promoForm.benefits.smsQuota,
          voice_quota:
            promoForm.benefits.voiceQuota === "" ? undefined : promoForm.benefits.voiceQuota,
          price_override:
            promoForm.benefits.priceOverride === "" ? undefined : promoForm.benefits.priceOverride,
          discount_percent:
            promoForm.benefits.discountPercent === "" ? undefined : promoForm.benefits.discountPercent,
          note: promoForm.benefits.note || undefined,
          duration_days:
            promoForm.benefits.durationDays === "" ? undefined : promoForm.benefits.durationDays,
          expires_at: promoForm.benefits.expiresAt || undefined,
        },
      };

      if (editingPromoId) {
        await axios.put(`${API_BASE_URL}/ops/promo-codes/${editingPromoId}`, payload, { headers });
        setPromoStatus("Promo updated.");
      } else {
        await axios.post(`${API_BASE_URL}/ops/promo-codes`, payload, { headers });
        setPromoStatus("Promo created.");
      }
      await loadPromoCodes();
      resetPromoForm();
    } catch (err) {
      console.error("OpsBilling:promo save error", err.response?.data || err.message);
      setPromoStatus(err.response?.data?.error || "Unable to save promo code.");
    } finally {
      setPromoLoading(false);
    }
  };

  const deletePromo = async (promo) => {
    if (!promo) return;
    openConfirm({
      title: `Delete Promo ${promo.code}`,
      message: "This action cannot be undone.",
      variant: "danger",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/ops/promo-codes/${promo.id}`, { headers });
          setPromoStatus("Promo deleted.");
          if (editingPromoId === promo.id) {
            resetPromoForm();
          }
          await loadPromoCodes();
        } catch (err) {
          console.error("OpsBilling:promo delete error", err.response?.data || err.message);
          setPromoStatus(err.response?.data?.error || "Unable to delete promo.");
        }
      },
    });
  };

  const sendInvoiceEmail = async (invoiceId) => {
    if (!selectedCompany) return;
    try {
      await axios.post(
        `${API_BASE_URL}/ops/companies/${selectedCompany.id}/invoices/${invoiceId}/send`,
        {},
        { headers }
      );
      setCustomerStatus("Invoice email sent.");
    } catch (err) {
      console.error("OpsBilling:send invoice error", err.response?.data || err.message);
      setCustomerError(err.response?.data?.error || "Unable to send invoice.");
    }
  };

  const confirmSendInvoice = (invoiceId) => {
    openConfirm({
      title: "Send Invoice Email",
      message: "Send this invoice to the customer via email?",
      confirmLabel: "Send Email",
      onConfirm: () => sendInvoiceEmail(invoiceId),
    });
  };

  const handleCreditInvoice = async (invoiceId) => {
    if (!selectedCompany) return;
    const amount = window.prompt("Credit amount (in dollars):", "10");
    if (!amount) return;
    const memo = window.prompt("Memo (optional):", "");
    try {
      await axios.post(
        `${API_BASE_URL}/ops/companies/${selectedCompany.id}/invoices/${invoiceId}/credit`,
        { amount, memo },
        { headers }
      );
      setCustomerStatus("Credit note created.");
      loadSubscription(selectedCompany);
    } catch (err) {
      console.error("OpsBilling:credit error", err.response?.data || err.message);
      setCustomerError(err.response?.data?.error || "Unable to create credit note.");
    }
  };

  const renderPlanCatalog = () => (
    <div className="ops-section">
      <div className="ops-section-header">
        <div>
          <h3>Plan Catalog</h3>
          <p className="text-muted">
            Drag and drop to change order. Edit any plan to update Stripe IDs or limits.
          </p>
        </div>
        {savingOrder && <span className="text-muted">Saving order...</span>}
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center py-5">Loading plans...</div>
      ) : (
        <div className="ops-plan-grid">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="ops-plan-card"
              draggable
              onDragStart={(e) => handleDragStart(e, plan.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, plan.id)}
            >
              <div className="ops-plan-card__header">
                <div>
                  <p className="eyebrow">Plan</p>
                  <h4>{plan.name}</h4>
                  <p className="price-line">
                    ${Number(plan.price).toFixed(2)} / {plan.billing_cycle}
                  </p>
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => openEditor(plan)}>
                  Edit
                </button>
              </div>
              <ul className="ops-plan-features">
                <li>{plan.devices_allowed || "Unlimited"} devices</li>
                <li>{plan.users_allowed || "Unlimited"} users</li>
                <li>{plan.priority_support ? "Priority support" : "Standard support"}</li>
                {(plan.features || []).map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              <div className="ops-plan-footer">
                <span className={`pill ${plan.is_public ? "pill-success" : "pill-muted"}`}>
                  {plan.is_public ? "Public" : "Hidden"}
                </span>
                <span className="pill pill-outline">Order #{plan.display_order}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderPlanEditor = () =>
    editingPlan && formValues && (
      <div className="ops-plan-modal">
        <div className="ops-plan-modal__card">
          <h4>Edit Plan: {formValues.name}</h4>
          {planStatus && (
            <div className={`alert ${planStatus.type === "error" ? "alert-danger" : "alert-success"}`}>
              <strong>{planStatus.type === "error" ? "Heads up:" : "All set:"}</strong> {planStatus.text}
              {planStatus.prorated && planStatus.type !== "error" && (
                <div className="text-muted small mt-1">
                  Existing subscriptions were prorated immediately.
                </div>
              )}
              {planStatus.failures && planStatus.failures.length > 0 && (
                <details className="mt-2">
                  <summary className="small">
                    {planStatus.failures.length} subscription
                    {planStatus.failures.length > 1 ? "s" : ""} need follow-up
                  </summary>
                  <ul className="mt-2">
                    {planStatus.failures.map((failure) => (
                      <li key={failure.subscriptionId || failure.message}>
                        <span className="mono-id">{failure.subscriptionId || "unknown"}</span> —{" "}
                        {failure.message || "Unable to update subscription"}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          <div className="modal-grid">
            <label>
              Name
              <input value={formValues.name || ""} onChange={(e) => handleFormChange("name", e.target.value)} />
            </label>
            <label>
              Description
              <input
                value={formValues.description || ""}
                onChange={(e) => handleFormChange("description", e.target.value)}
              />
            </label>
            <label>
              Price
              <input
                type="number"
                value={formValues.price}
                onChange={(e) => handleFormChange("price", e.target.value)}
              />
            </label>
            <label>
              Billing cycle
              <select
                value={formValues.billing_cycle || "monthly"}
                onChange={(e) => handleFormChange("billing_cycle", e.target.value)}
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
            </label>
            <label>
              Devices allowed
              <input
                type="number"
                value={formValues.devices_allowed}
                onChange={(e) => handleFormChange("devices_allowed", e.target.value)}
              />
            </label>
            <label>
              Users allowed
              <input
                type="number"
                value={formValues.users_allowed}
                onChange={(e) => handleFormChange("users_allowed", e.target.value)}
              />
            </label>
            <label>
              Stripe product ID
              <input
                value={formValues.stripe_product_id || ""}
                onChange={(e) => handleFormChange("stripe_product_id", e.target.value)}
              />
            </label>
            <label>
              Stripe price ID
              <input
                value={formValues.stripe_price_id || ""}
                onChange={(e) => handleFormChange("stripe_price_id", e.target.value)}
              />
            </label>
            <label>
              Display order
              <input
                type="number"
                value={formValues.display_order}
                onChange={(e) => handleFormChange("display_order", Number(e.target.value))}
              />
            </label>
            <label>
              Priority support
              <select
                value={formValues.priority_support ? "true" : "false"}
                onChange={(e) => handleFormChange("priority_support", e.target.value === "true")}
              >
                <option value="true">Enabled</option>
                <option value="false">Standard</option>
              </select>
            </label>
            <label>
              Visibility
              <select
                value={formValues.is_public ? "true" : "false"}
                onChange={(e) => handleFormChange("is_public", e.target.value === "true")}
              >
                <option value="true">Public</option>
                <option value="false">Hidden</option>
              </select>
            </label>
            <label className="full-width">
              Features (one per line)
              <textarea
                rows={6}
                value={formValues.featuresText}
                onChange={(e) => handleFormChange("featuresText", e.target.value)}
              />
            </label>
            <div className="full-width form-check mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="planProrateToggle"
                checked={Boolean(formValues.prorateMigrations)}
                onChange={(e) => handleFormChange("prorateMigrations", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="planProrateToggle">
                Charge proration immediately when migrating existing subscriptions
              </label>
              <div className="form-text">
                Keep this off to bill the new amount on the next renewal without mid-cycle charges.
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-primary" onClick={savePlan}>
              Save
            </button>
            <button className="btn btn-outline-secondary" onClick={closeEditor}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );

  const renderCustomerControls = () => (
    <div className="ops-section">
      <div className="ops-section-header">
        <div>
          <h3>Customer Accounts</h3>
          <p className="text-muted">
            Search for a company to view billing details, refresh subscription status, or send invoices.
          </p>
        </div>
      </div>
      <form
        className="ops-search"
        onSubmit={(e) => {
          e.preventDefault();
          searchCustomers();
        }}
      >
        <input
          type="text"
          placeholder="Search companies by name or email"
          value={customerSearch}
          onChange={(e) => setCustomerSearch(e.target.value)}
        />
        <button className="btn btn-primary" type="submit" disabled={customerLoading}>
          {customerLoading ? "Searching..." : "Search"}
        </button>
      </form>
      {customerError && <div className="alert alert-danger mt-3">{customerError}</div>}
      {customerResults.length > 0 && (
        <div className="ops-table-wrapper mt-3">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company ID</th>
                <th>Email</th>
                <th>Plan</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customerResults.map((company) => (
                <tr key={company.id}>
                  <td>{company.name}</td>
                  <td className="mono-id">{company.id}</td>
                  <td>{company.email}</td>
                  <td>{company.plan_name || "Unassigned"}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => loadSubscription(company)}>
                      Load
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCompany && subscription && (
        <div className="ops-customer-panel">
          <div className="ops-customer-header">
            <div>
              <h4>{selectedCompany.name}</h4>
              <p className="mono-id">{selectedCompany.id}</p>
              {customerStatus && <span className="text-success">{customerStatus}</span>}
            </div>
            <div className="ops-customer-actions">
              <button className="btn btn-outline-secondary" onClick={() => runCustomerAction({ type: "portal" })}>
                Copy Portal Link
              </button>
              <button className="btn btn-outline-primary" onClick={() => runCustomerAction({ type: "refresh" })}>
                Refresh Status
              </button>
              <button className="btn btn-outline-danger" onClick={() => runCustomerAction({ type: "cancel" })}>
                Cancel Plan
              </button>
              <button className="btn btn-outline-success" onClick={() => runCustomerAction({ type: "resume" })}>
                Resume Plan
              </button>
            </div>
          </div>
          <div className="ops-detail-grid">
            <div className="ops-detail-card">
              <strong>Subscription</strong>
              <p>Status: {subscription.subscription?.status || "unknown"}</p>
              <p>
                Current period: {subscription.subscription?.currentPeriodStart || "—"} →{" "}
                {subscription.subscription?.currentPeriodEnd || "—"}
              </p>
              <p>Auto renew: {subscription.subscription?.autoRenew === false ? "disabled" : "enabled"}</p>
            </div>
            <div className="ops-detail-card">
              <strong>Plan</strong>
              <p>Name: {subscription.plan?.name || "Unassigned"}</p>
              <p>Devices allowed: {subscription.plan?.devicesAllowed || "Unlimited"}</p>
              <p>Users allowed: {subscription.plan?.usersAllowed || "Unlimited"}</p>
            </div>
          </div>
          <div className="ops-table-wrapper mt-3">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(subscription.invoices || []).map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="mono-id">{invoice.stripe_invoice_id || invoice.id}</td>
                    <td>
                      {invoice.period_start || "—"} → {invoice.period_end || "—"}
                    </td>
                    <td>
                      {invoice.amount_due ? `$${Number(invoice.amount_due).toFixed(2)}` : "--"}{" "}
                      {invoice.currency?.toUpperCase()}
                    </td>
                    <td>{invoice.status}</td>
                    <td className="d-flex gap-2 flex-wrap">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => confirmSendInvoice(invoice.stripe_invoice_id || invoice.id)}
                      >
                        Send Email
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleCreditInvoice(invoice.stripe_invoice_id || invoice.id)}
                      >
                        Issue Credit
                      </button>
                    </td>
                  </tr>
                ))}
                {(!subscription.invoices || subscription.invoices.length === 0) && (
                  <tr>
                    <td colSpan={5}>No invoices found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="ops-billing">
        <div className="ops-billing-tabs">
          {Object.entries(TABS).map(([key, label]) => (
            <button
              key={key}
              className={activeTab === key ? "active" : ""}
              onClick={() => setActiveTab(key)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
        {activeTab === "plans" ? (
          renderPlanCatalog()
        ) : activeTab === "customers" ? (
          renderCustomerControls()
        ) : (
          <OpsOverridesTab
            overrideSearch={overrideSearch}
            setOverrideSearch={setOverrideSearch}
            overrideCompanyError={overrideCompanyError}
            overrideCompanies={overrideCompanies}
            overrideCompanyLoading={overrideCompanyLoading}
            searchOverrideCompanies={searchOverrideCompanies}
            overrideCompany={overrideCompany}
            selectOverrideCompany={selectOverrideCompany}
            overrideForm={overrideForm}
            handleOverrideChange={handleOverrideChange}
            overrideStatus={overrideStatus}
            overrideSaving={overrideSaving}
            activeOverride={activeOverride}
            deleteOverride={deleteOverride}
            saveOverride={saveOverride}
            promoRedeemCode={promoRedeemCode}
            setPromoRedeemCode={setPromoRedeemCode}
            redeemPromo={redeemPromo}
            promoCodes={promoCodes}
            promoLoading={promoLoading}
            openPromoEditor={openPromoEditor}
            deletePromo={deletePromo}
            promoStatus={promoStatus}
            editingPromoId={editingPromoId}
            promoForm={promoForm}
            handlePromoField={handlePromoField}
            handlePromoBenefitField={handlePromoBenefitField}
            resetPromoForm={resetPromoForm}
            savePromo={savePromo}
          />
        )}
        {renderPlanEditor()}
      </div>
      <ConfirmDialog {...confirmProps} />
    </>
  );
}
